import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExternalLink } from "react-icons/hi";
import type { Invoice } from "../../models";
import type { AccountingConnection, AccountingInvoiceStatus } from "../../api";
import {
  getInvoiceDeliveryEntries,
  getInvoiceIssuer,
  getLatestInvoiceDelivery,
  type InvoiceDeliveryDocument,
  type InvoiceDeliveryEntry,
} from "../../helpers/invoiceDelivery";
import { isInvoiceFinalized } from "../../helpers/invoiceFilters";
import {
  getExternalOrderUrl,
  getIntegrationByProviderKey,
} from "../../integrations/catalog";
import {
  formatInvoiceMoney,
  getInvoiceSettlementAmounts,
} from "../../helpers/invoiceSettlement";

interface InvoiceDeliveryTraceProps {
  invoice: Invoice;
  accountingStatus?: AccountingInvoiceStatus | null;
  accountingConnection?: AccountingConnection | null;
  accountingStatusLoading?: boolean;
}

interface InvoiceActivityEntry {
  id: string;
  title: string;
  detail?: string;
  at?: string;
}

interface InvoiceDeliverySummaryEntry {
  id: string;
  title: string;
  detail: string;
  tone: "success" | "info" | "warning" | "neutral";
  externalUrl?: string;
}

const formatDeliveryDate = (
  value: string | undefined,
  language: string,
): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const locale =
    language.startsWith("no") || language.startsWith("nb") ? "nb-NO" : "en-GB";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const documentTranslationKey = (document: InvoiceDeliveryDocument): string =>
  `invoice:delivery_trace.documents.${document}`;

const getDeliveryDetail = (
  entry: InvoiceDeliveryEntry,
  language: string,
  translate: (key: string) => string,
): string => {
  return [
    entry.recipientEmail ||
      translate("invoice:delivery_trace.unknown_recipient"),
    formatDeliveryDate(entry.sentAt, language),
  ]
    .filter(Boolean)
    .join(" · ");
};

const getSettlementDetail = (
  status: string | null | undefined,
  amount: string | number | null | undefined,
  amountOutstanding: string | number | null | undefined,
  language: string,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string | undefined => {
  const amounts = getInvoiceSettlementAmounts(amount, amountOutstanding);
  if (amounts == null) return undefined;

  if (status === "credited") {
    return translate("invoice:accounting_integration.credit_progress", {
      credited: formatInvoiceMoney(amounts.resolvedAmount, language),
      outstanding: formatInvoiceMoney(amounts.remainingAmount, language),
    });
  }
  if (status === "overpaid" && amounts.overpaidAmount > 0) {
    return translate("invoice:accounting_integration.overpayment_progress", {
      paid: formatInvoiceMoney(amounts.resolvedAmount, language),
      overpaid: formatInvoiceMoney(amounts.overpaidAmount, language),
    });
  }
  return translate("invoice:accounting_integration.payment_progress", {
    paid: formatInvoiceMoney(amounts.resolvedAmount, language),
    outstanding: formatInvoiceMoney(amounts.remainingAmount, language),
  });
};

const summaryDotStyle: Record<InvoiceDeliverySummaryEntry["tone"], string> = {
  success:
    "bg-lgb-green-300 ring-2 ring-lgb-green-100 dark:ring-lgb-green-200/40",
  info: "bg-lgb-blue-400",
  warning: "bg-lgb-yellow-400",
  neutral: "bg-lgb-grey-400",
};

const hasRegistrationActivity = (
  accountingStatus?: AccountingInvoiceStatus | null,
): boolean =>
  Boolean(
    accountingStatus?.activity?.some(
      (entry) => entry.type === "registration_confirmed",
    ),
  );

export const getInvoiceActivityCount = (
  invoice: Invoice,
  accountingStatus?: AccountingInvoiceStatus | null,
): number =>
  getInvoiceDeliveryEntries(invoice).length +
  (accountingStatus?.activity?.length ?? 0) +
  (invoice.accountingState === "confirmed" &&
  !hasRegistrationActivity(accountingStatus)
    ? 1
    : 0);

export const InvoiceDeliverySummary: FC<InvoiceDeliveryTraceProps> = ({
  invoice,
  accountingStatus,
  accountingConnection,
  accountingStatusLoading = false,
}) => {
  const { t, i18n } = useTranslation(["invoice"]);
  const issuer = getInvoiceIssuer(invoice);
  const entries = getInvoiceDeliveryEntries(invoice);
  const latestCustomerDelivery = getLatestInvoiceDelivery(entries, "customer");
  const latestAccountingDelivery = getLatestInvoiceDelivery(
    entries,
    "accounting",
  );
  const accountingDate = formatDeliveryDate(
    invoice.accountingConfirmedAt,
    i18n.language,
  );
  const accountingState =
    accountingStatus?.state ??
    invoice.accountingState ??
    (isInvoiceFinalized(invoice) ? "not_registered" : "not_applicable");
  const accountingMode = accountingStatus?.mode ?? invoice.accountingMode;
  const providerKey =
    accountingStatus?.providerKey ?? invoice.accountingProviderKey;
  const isDirectAccountingIntegration =
    issuer === "accounting_provider" &&
    (accountingMode === "integration" || Boolean(providerKey));
  const provider =
    getIntegrationByProviderKey(providerKey)?.name ||
    t("invoice:accounting_integration.accounting_system");
  const externalOrderUrl =
    accountingConnection &&
    providerKey &&
    accountingConnection.providerKey === providerKey
      ? getExternalOrderUrl(providerKey, {
          environment: accountingConnection.providerEnvironment,
          externalCompanyId: accountingConnection.externalCompanyId,
          externalOrderId: accountingStatus?.externalDocument?.orderId,
        })
      : undefined;
  const plannedCustomerRecipient =
    issuer !== "accounting_provider" &&
    (invoice.invoicee?.invoiceEmail || invoice.invoicee?.contactEmail)
      ? t("invoice:delivery_trace.planned_recipient", {
          email: invoice.invoicee.invoiceEmail || invoice.invoicee.contactEmail,
        })
      : undefined;
  const providerDeliveryUnknown =
    issuer === "accounting_provider" &&
    accountingStatus?.externalDocument?.invoiceId &&
    (!accountingStatus.delivery ||
      accountingStatus.delivery.status === "unknown");
  const customerDeliveryItem: InvoiceDeliverySummaryEntry = {
    id: "customer",
    title: latestCustomerDelivery
      ? t(documentTranslationKey(latestCustomerDelivery.document))
      : t(
          providerDeliveryUnknown
            ? "invoice:accounting_integration.provider_delivery"
            : "invoice:delivery_trace.customer",
        ),
    detail: latestCustomerDelivery
      ? getDeliveryDetail(latestCustomerDelivery, i18n.language, t)
      : providerDeliveryUnknown
        ? t("invoice:accounting_integration.provider_delivery_unknown", {
            provider,
          })
        : [
            t(
              issuer === "accounting_provider"
                ? "invoice:delivery_trace.customer_not_sent_by_logibud"
                : "invoice:delivery_trace.not_sent",
            ),
            plannedCustomerRecipient,
          ]
            .filter(Boolean)
            .join(" · "),
    tone: latestCustomerDelivery ? "success" : "neutral",
  };
  const accountingDeliveryItem: InvoiceDeliverySummaryEntry = {
    id: "accounting-inbox",
    title: latestAccountingDelivery
      ? t(documentTranslationKey(latestAccountingDelivery.document))
      : t("invoice:delivery_trace.accounting_inbox"),
    detail: latestAccountingDelivery
      ? getDeliveryDetail(latestAccountingDelivery, i18n.language, t)
      : t("invoice:delivery_trace.not_sent"),
    tone: latestAccountingDelivery ? "success" : "neutral",
  };
  const deliveryItems: InvoiceDeliverySummaryEntry[] = [
    ...(latestCustomerDelivery || !isDirectAccountingIntegration
      ? [customerDeliveryItem]
      : []),
    ...(latestAccountingDelivery || !isDirectAccountingIntegration
      ? [accountingDeliveryItem]
      : []),
  ];
  const integrationItems: InvoiceDeliverySummaryEntry[] =
    accountingMode === "integration" || providerKey
      ? [
          {
            id: "accounting-integration",
            title: accountingStatusLoading
              ? t("invoice:accounting_integration.loading")
              : t(
                  accountingStatus?.error || accountingState === "failed"
                    ? "invoice:accounting_integration.sync_error"
                    : accountingState === "pending"
                      ? "invoice:accounting_integration.creating"
                      : accountingState === "submitted"
                        ? "invoice:accounting_integration.verifying"
                        : "invoice:accounting_integration.created",
                  { provider },
                ),
            detail:
              accountingStatus?.error?.message ||
              (accountingStatus?.externalDocument?.invoiceNumber
                ? t("invoice:accounting_integration.external_invoice", {
                    provider,
                    number: accountingStatus.externalDocument.invoiceNumber,
                  })
                : t("invoice:accounting_integration.provider_detail", {
                    provider,
                  })),
            tone:
              accountingStatus?.error || accountingState === "failed"
                ? "warning"
                : accountingState === "confirmed"
                  ? "success"
                  : "info",
            externalUrl: externalOrderUrl,
          },
          ...(accountingStatus?.externalDocument?.invoiceId
            ? [
                {
                  id: "accounting-settlement",
                  title: t(
                    `invoice:accounting_integration.settlement.${
                      accountingStatus.settlement?.status || "unknown"
                    }`,
                  ),
                  detail: [
                    getSettlementDetail(
                      accountingStatus.settlement?.status,
                      accountingStatus.settlement?.amount,
                      accountingStatus.settlement?.amountOutstanding,
                      i18n.language,
                      t,
                    ),
                    formatDeliveryDate(
                      accountingStatus.settlement?.observedAt || undefined,
                      i18n.language,
                    ),
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  tone:
                    accountingStatus.settlement?.status === "settled"
                      ? ("success" as const)
                      : accountingStatus.settlement?.status === "credited" ||
                          accountingStatus.settlement?.status === "overpaid" ||
                          accountingStatus.settlement?.status ===
                            "partially_settled"
                        ? ("warning" as const)
                        : ("info" as const),
                },
                {
                  id: "accounting-synchronization",
                  title: t("invoice:accounting_integration.last_synchronized"),
                  detail:
                    formatDeliveryDate(
                      accountingStatus.synchronization?.lastReconciledAt ||
                        undefined,
                      i18n.language,
                    ) || t("invoice:accounting_integration.not_synchronized"),
                  tone: "neutral" as const,
                },
              ]
            : []),
        ]
      : [
          {
            id: "accounting-registration",
            title: t(
              `invoice:delivery_trace.accounting_states.${accountingState}`,
            ),
            detail: [
              t("invoice:delivery_trace.accounting_registration"),
              ...(accountingState === "confirmed"
                ? [t("invoice:delivery_trace.marked_manually"), accountingDate]
                : []),
            ]
              .filter(Boolean)
              .join(" · "),
            tone:
              accountingState === "confirmed"
                ? ("success" as const)
                : accountingState === "failed"
                  ? ("warning" as const)
                  : accountingState === "pending" ||
                      accountingState === "submitted"
                    ? ("info" as const)
                    : ("neutral" as const),
          },
        ];
  const summaryItems = [...deliveryItems, ...integrationItems];

  return (
    <div>
      <ol className="divide-y divide-border">
        {summaryItems.map((item) => (
          <li key={item.id} className="flex gap-3 px-4 py-4">
            <span
              aria-hidden="true"
              className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${summaryDotStyle[item.tone]}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <p className="text-sm font-medium text-text">{item.title}</p>
                {item.externalUrl ? (
                  <a
                    href={item.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand hover:underline focus-visible:rounded focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    {t("invoice:accounting_integration.open_in_provider", {
                      provider,
                    })}
                    <HiOutlineExternalLink
                      aria-hidden="true"
                      className="h-4 w-4"
                    />
                  </a>
                ) : null}
              </div>
              <p className="mt-0.5 break-words text-xs leading-5 text-muted">
                {item.detail}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const InvoiceActivity: FC<InvoiceDeliveryTraceProps> = ({
  invoice,
  accountingStatus,
}) => {
  const { t, i18n } = useTranslation(["invoice"]);
  const deliveryEntries: InvoiceActivityEntry[] = getInvoiceDeliveryEntries(
    invoice,
  ).map((entry, index) => ({
    id: `${entry.channel}-${entry.sentAt ?? "unknown"}-${index}`,
    title: t(documentTranslationKey(entry.document)),
    detail:
      entry.recipientEmail || t("invoice:delivery_trace.unknown_recipient"),
    at: entry.sentAt,
  }));
  const integrationEntries: InvoiceActivityEntry[] = (
    accountingStatus?.activity ?? []
  ).map((entry, index) => {
    if (entry.type === "registration_confirmed") {
      return {
        id: `${entry.type}-${entry.at ?? "unknown"}-${index}`,
        title: t("invoice:delivery_trace.accounting_states.confirmed"),
        detail: t("invoice:delivery_trace.via_integration"),
        at: entry.at ?? undefined,
      };
    }
    return {
      id: `${entry.type}-${entry.at ?? "unknown"}-${index}`,
      title: t(
        `invoice:accounting_integration.settlement.${
          entry.status || "unknown"
        }`,
      ),
      detail: getSettlementDetail(
        entry.status,
        accountingStatus?.settlement?.amount,
        entry.amountOutstanding,
        i18n.language,
        t,
      ),
      at: entry.at ?? undefined,
    };
  });
  const fallbackAccountingEntries: InvoiceActivityEntry[] =
    invoice.accountingState === "confirmed" &&
    !hasRegistrationActivity(accountingStatus)
      ? [
          {
            id: `accounting-confirmed-${invoice.accountingConfirmedAt ?? "unknown"}`,
            title: t("invoice:delivery_trace.accounting_states.confirmed"),
            detail: t(
              invoice.accountingMode === "integration"
                ? "invoice:delivery_trace.via_integration"
                : "invoice:delivery_trace.marked_manually",
            ),
            at: invoice.accountingConfirmedAt,
          },
        ]
      : [];
  const activity = [
    ...deliveryEntries,
    ...integrationEntries,
    ...fallbackAccountingEntries,
  ].sort(
    (left, right) =>
      new Date(right.at ?? 0).getTime() - new Date(left.at ?? 0).getTime(),
  );

  if (activity.length === 0) {
    return (
      <p className="px-4 py-8 text-sm text-muted">
        {t("invoice:delivery_trace.no_activity")}
      </p>
    );
  }

  return (
    <ol className="divide-y divide-border">
      {activity.map((entry) => (
        <li
          key={entry.id}
          className="grid gap-2 px-4 py-4 sm:grid-cols-[8rem_minmax(0,1fr)] sm:px-0"
        >
          <time className="text-sm text-muted">
            {formatDeliveryDate(entry.at, i18n.language) ||
              t("invoice:delivery_trace.unknown_time")}
          </time>
          <div className="min-w-0">
            <p className="font-medium text-text">{entry.title}</p>
            {entry.detail && (
              <p className="mt-0.5 break-words text-sm text-muted">
                {entry.detail}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

/**
 * Compatibility composition for callers that still need the old combined
 * delivery trace. Invoice details use the focused summary and activity panels.
 */
export const InvoiceDeliveryTrace: FC<InvoiceDeliveryTraceProps> = ({
  invoice,
}) => {
  const { t } = useTranslation(["invoice"]);

  return (
    <section className="my-6" aria-labelledby="invoice-delivery-heading">
      <h2
        id="invoice-delivery-heading"
        className="mb-4 text-2xl font-medium text-text"
      >
        {t("invoice:delivery_trace.title")}
      </h2>
      <InvoiceDeliverySummary invoice={invoice} />
      <h3 className="mb-2 mt-6 text-xl font-medium text-text">
        {t("invoice:delivery_trace.history")}
      </h3>
      <InvoiceActivity invoice={invoice} />
    </section>
  );
};
